/**
 * Custom hook for accessing and managing user statistics
 */

import { useContext, useMemo } from 'react';
import { AuctionContext } from '../context/AuctionContext';
import { useWallet } from '@solana/wallet-adapter-react';

/**
 * Hook for accessing user stats from the auction context
 * @returns {Object} User stats and related flags
 */
export const useUserStats = () => {
  const { 
    getUserStats, 
    userContributions, 
    projectedContributions,
    currentDay
  } = useContext(AuctionContext);
  
  const wallet = useWallet();
  
  // Memoize user stats to prevent unnecessary recalculations
  const userStats = useMemo(() => {
    if (!wallet.connected) return {
      totalContribution: 0,
      pendingContribution: 0,
      confirmedJinu: 0,
      pendingJinu: 0,
      sharePercentage: 0,
      daysParticipated: 0
    };
    
    return getUserStats() || {};
  }, [getUserStats, wallet.connected]);
  
  // Determine if user has confirmed or projected contributions
  const hasConfirmedContributions = userStats.hasConfirmedContributions || userStats.totalContribution > 0;
  const hasProjectedContributions = userStats.pendingContribution > 0;
  
  return {
    userStats,
    hasConfirmedContributions,
    hasProjectedContributions,
    currentDay
  };
};
